import { classNames } from '@commenda/utils';
import { useEffect, useMemo, useRef } from 'react';
import { CalendarPreviewHelper } from '../../helpers';
import {
  CalendarPreviewTableProps,
  CoverageGapEntry,
  MonthData,
  PositionedFiling,
} from '../../types';
import { FilingInfoCard } from '../FilingCard/FilingInfoCard';
import { SkippedFilingCard } from '../FilingCard/SkippedFilingCard';

// Tall enough for 4 text lines (title, badges, due, period) + card padding
// without clipping or bleeding into the next row.
const MONTH_ROW_HEIGHT = 140;
const CARD_TOP_PX = 12;
const CARD_SIDE_PX = 12;
// Thin gutter between adjacent lane slots so cards don't visually touch.
const LANE_GUTTER_PX = 4;

function laneSlotStyle(
  laneIndex: number,
  clusterLaneCount: number
): { left: string; width: string } {
  const count = Math.max(1, clusterLaneCount);
  const horizontalPadding = CARD_SIDE_PX * 2;
  const totalGutter = LANE_GUTTER_PX * (count - 1);
  const slotWidthExpr = `calc((100% - ${horizontalPadding}px - ${totalGutter}px) / ${count})`;
  const slotOffsetExpr = `calc(${CARD_SIDE_PX}px + ${laneIndex} * (${slotWidthExpr} + ${LANE_GUTTER_PX}px))`;
  return { left: slotOffsetExpr, width: slotWidthExpr };
}

export function CalendarPreviewTable({
  months,
  newCalendarFilings,
  oldCalendarFilings,
  coverageGaps,
  taxPeriodStart,
  taxPeriodEnd,
  onYearInView,
  onFilingDeleted,
  corporationId,
}: CalendarPreviewTableProps) {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const yearRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Position filings with their spans, then assign lanes so overlapping
  // filings render staggered instead of z-stacked on top of each other.
  const positionedNewFilings = useMemo(
    () =>
      CalendarPreviewHelper.assignLanesToPositionedFilings(
        CalendarPreviewHelper.positionFilingsForGrid(newCalendarFilings, months)
      ),
    [newCalendarFilings, months]
  );

  const positionedOldFilings = useMemo(
    () =>
      CalendarPreviewHelper.assignLanesToPositionedFilings(
        oldCalendarFilings
          ? CalendarPreviewHelper.positionFilingsForGrid(oldCalendarFilings, months)
          : []
      ),
    [oldCalendarFilings, months]
  );

  const positionedGaps = useMemo(() => {
    if (!coverageGaps) return [];

    return coverageGaps.map((gap) => {
      const start = CalendarPreviewHelper.isoStringToMonthYear(gap.tax_period_start_date);
      const end = CalendarPreviewHelper.isoStringToMonthYear(gap.tax_period_end_date);

      const startMonthIndex = months.findIndex(
        (m) => m.year === start.year && m.index + 1 === start.month
      );
      const endMonthIndex = months.findIndex(
        (m) => m.year === end.year && m.index + 1 === end.month
      );

      const rowSpan =
        endMonthIndex >= 0 && startMonthIndex >= 0
          ? endMonthIndex - startMonthIndex + 1
          : 1;

      return {
        gap,
        startMonthIndex: Math.max(0, startMonthIndex),
        rowSpan: Math.max(1, rowSpan),
      };
    });
  }, [coverageGaps, months]);

  // Calculate tax period range indices
  const taxPeriodRange = useMemo(() => {
    if (!taxPeriodStart) return null;

    // month.index is 0-based (0-11), taxPeriodStart.month is 1-based (1-12)
    const startIndex = months.findIndex(
      (m) =>
        m.year === taxPeriodStart.year && m.index + 1 === taxPeriodStart.month
    );

    if (startIndex === -1) {
      return null;
    }

    let endIndex = startIndex;
    if (taxPeriodEnd) {
      endIndex = months.findIndex(
        (m) =>
          m.year === taxPeriodEnd.year && m.index + 1 === taxPeriodEnd.month
      );
      if (endIndex === -1) {
        // If end month not found, use the last month
        endIndex = months.length - 1;
      }
    } else {
      // If no end date, span to the last month
      endIndex = months.length - 1;
    }

    return { startIndex, endIndex };
  }, [taxPeriodStart, taxPeriodEnd, months]);

  // Group months by year WITH their global indices
  const monthsByYearWithIndex = useMemo(() => {
    const result = new Map<
      number,
      Array<{ month: MonthData; globalIndex: number }>
    >();

    months.forEach((month, globalIndex) => {
      if (!result.has(month.year)) {
        result.set(month.year, []);
      }
      result.get(month.year)!.push({ month, globalIndex });
    });

    return result;
  }, [months]);

  // Set up intersection observer
  useEffect(() => {
    if (!onYearInView) return undefined;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const year = parseInt(
              entry.target.getAttribute('data-year') ?? '0',
              10
            );
            if (year) {
              onYearInView(year);
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    yearRefs.current.forEach((element) => {
      observerRef.current?.observe(element);
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [onYearInView, months]);

  // Get filings that START in a specific month (for rendering)
  const getFilingsStartingInMonth = (
    positionedFilings: PositionedFiling[],
    globalMonthIndex: number
  ): PositionedFiling[] => {
    return positionedFilings.filter(
      (pf) => pf.startMonthIndex === globalMonthIndex
    );
  };

  // Get coverage gaps that START in a specific month (for rendering)
  const getGapsStartingInMonth = (
    globalMonthIndex: number
  ): Array<{ gap: CoverageGapEntry; rowSpan: number }> => {
    return positionedGaps
      .filter((pg) => pg.startMonthIndex === globalMonthIndex)
      .map((pg) => ({ gap: pg.gap, rowSpan: pg.rowSpan }));
  };

  // Check if a month is the start of the tax period
  const isTaxPeriodStart = (globalIndex: number): boolean => {
    if (!taxPeriodRange) return false;
    return globalIndex === taxPeriodRange.startIndex;
  };

  // Check if a month is the end of the tax period
  const isTaxPeriodEnd = (globalIndex: number): boolean => {
    if (!taxPeriodRange) return false;
    return globalIndex === taxPeriodRange.endIndex;
  };

  const years = Array.from(monthsByYearWithIndex.keys()).sort((a, b) => a - b);

  return (
    <div className="w-full">
      <div className="grid grid-cols-[120px_1fr_1fr] sticky top-0 bg-bg-secondary border-b border-border-primary z-20">
        <div className="p-3 txt-small-medium text-text-secondary">Month</div>
        <div className="p-3 txt-small-medium text-text-secondary border-l border-border-primary">
          Proposed Calendar
        </div>
        <div className="p-3 txt-small-medium text-text-secondary border-l border-border-primary">
          Existing Calendar
        </div>
      </div>

      {years.map((year) => {
        const yearMonthsWithIndex = monthsByYearWithIndex.get(year) ?? [];

        return (
          <div
            key={year}
            data-year={year}
            ref={(el) => {
              if (el) {
                yearRefs.current.set(year, el);
              } else {
                yearRefs.current.delete(year);
              }
            }}
          >
            <div
              className={classNames(
                'sticky top-[44px] z-10 bg-bg-tertiary border-b border-border-primary',
                'py-2 px-3'
              )}
            >
              <span className="txt-medium-bold text-text-primary">{year}</span>
            </div>

            <div className="divide-y divide-border-primary relative">
              {yearMonthsWithIndex.map(({ month, globalIndex }) => {
                const newFilingsForMonth = getFilingsStartingInMonth(
                  positionedNewFilings,
                  globalIndex
                );
                const oldFilingsForMonth = getFilingsStartingInMonth(
                  positionedOldFilings,
                  globalIndex
                );
                const gapsForMonth = getGapsStartingInMonth(globalIndex);
                const isStart = isTaxPeriodStart(globalIndex);
                const isEnd = isTaxPeriodEnd(globalIndex);

                return (
                  <div
                    key={`${month.year}-${month.index}`}
                    data-month={`${month.year}-${month.index + 1}`}
                    className="grid grid-cols-[120px_1fr_1fr] relative"
                    style={{ minHeight: `${MONTH_ROW_HEIGHT}px` }}
                  >
                    {isStart && taxPeriodRange && (
                      <div className="absolute left-0 right-0 top-0 h-0.5 bg-utility-orange-600 z-30">
                        <div className="absolute left-0 top-0 -translate-y-1/2 z-40">
                          <div className="bg-utility-orange-600 text-white txt-extra-small-regular px-2 py-0.5 rounded-sm">
                            Start
                          </div>
                        </div>
                      </div>
                    )}

                    {isEnd && taxPeriodRange && taxPeriodEnd && (
                      <div className="absolute left-0 right-0 bottom-0 h-0.5 bg-utility-orange-600 z-30">
                        <div className="absolute left-0 top-0 -translate-y-1/2 z-40">
                          <div className="bg-utility-orange-600 text-white txt-extra-small-regular px-2 py-0.5 rounded-sm">
                            End
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="p-3 flex flex-col gap-2 relative z-0">
                      <span className="txt-small-medium text-text-primary">
                        {month.fullName}
                      </span>
                    </div>
                    {/* New Calendar Column */}
                    <div className="p-3 border-l border-border-primary relative">
                      {gapsForMonth.map((g) => (
                        <div
                          key={`gap-${g.gap.tax_period_start_date}-${g.gap.tax_period_end_date}`}
                          className="absolute z-[5]"
                          style={{
                            top: `${CARD_TOP_PX}px`,
                            left: `${CARD_SIDE_PX}px`,
                            right: `${CARD_SIDE_PX}px`,
                            height: `calc(${g.rowSpan * MONTH_ROW_HEIGHT}px + ${g.rowSpan - 1}px - 24px)`,
                          }}
                        >
                          <SkippedFilingCard />
                        </div>
                      ))}

                      {newFilingsForMonth.map((pf, idx) => {
                        const slot = laneSlotStyle(
                          pf.laneIndex,
                          pf.clusterLaneCount
                        );
                        return (
                          <div
                            key={
                              pf.filing.id ||
                              `new-${globalIndex}-${pf.laneIndex}-${idx}`
                            }
                            className="absolute"
                            style={{
                              top: `${CARD_TOP_PX}px`,
                              left: slot.left,
                              width: slot.width,
                              height: `calc(${pf.rowSpan * MONTH_ROW_HEIGHT}px + ${pf.rowSpan - 1}px - 24px)`,
                              zIndex: 10 + pf.laneIndex,
                            }}
                          >
                            <FilingInfoCard
                              filing={pf.filing}
                              variant="new"
                              rowSpan={pf.rowSpan}
                            />
                          </div>
                        );
                      })}
                    </div>
                    {/* Old Calendar Column */}
                    <div className="p-3 border-l border-border-primary bg-utility-warning-50/30 relative">
                      {oldFilingsForMonth.map((pf, idx) => {
                        const slot = laneSlotStyle(
                          pf.laneIndex,
                          pf.clusterLaneCount
                        );
                        return (
                          <div
                            key={
                              pf.filing.id ||
                              `old-${globalIndex}-${pf.laneIndex}-${idx}`
                            }
                            className="absolute"
                            style={{
                              top: `${CARD_TOP_PX}px`,
                              left: slot.left,
                              width: slot.width,
                              height: `calc(${pf.rowSpan * MONTH_ROW_HEIGHT}px + ${pf.rowSpan - 1}px - 24px)`,
                              zIndex: 10 + pf.laneIndex,
                            }}
                          >
                            <FilingInfoCard
                              filing={pf.filing}
                              variant="existing"
                              rowSpan={pf.rowSpan}
                              onDelete={
                                onFilingDeleted
                                  ? () => onFilingDeleted()
                                  : undefined
                              }
                              corporationId={corporationId}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
